import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

/**
 * Outbound B2B leads (property-management companies sourced from CSV).
 *
 * Distinct from questionnaireService's leads, which are inbound
 * questionnaire submissions stored in `questionnaire_progress`.
 */
class LeadService {
  authHeaders() {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * @param {Object} params
   * @param {string} [params.status]
   * @param {string} [params.city]
   * @param {'true'|'false'} [params.hasEmail]
   * @param {string} [params.search] partial company-name match
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @returns {Promise<{leads: Object[], pagination: Object}>}
   */
  async listLeads(params = {}) {
    // Drop empty filters so they don't reach the API as `?status=`, which
    // fails the enum validator.
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value != null)
    );

    const response = await axios.get(`${API_BASE_URL}/api/admin/leads`, {
      headers: this.authHeaders(),
      params: query
    });

    logger.log('[LeadService] Loaded leads:', response.data.pagination);
    return response.data;
  }

  /** @returns {Promise<Object>} totals plus counts by status */
  async getStats() {
    const response = await axios.get(`${API_BASE_URL}/api/admin/leads/stats`, {
      headers: this.authHeaders()
    });
    return response.data.stats;
  }

  /**
   * Updates outreach state. Sourced fields (company, address, phone, email)
   * are owned by the importer and rejected by the API.
   *
   * @param {string} id
   * @param {{status?: string, notes?: string, lastContactedAt?: string, emailVerified?: boolean}} updates
   * @returns {Promise<Object>} the updated lead
   */
  async updateLead(id, updates) {
    const response = await axios.patch(`${API_BASE_URL}/api/admin/leads/${id}`, updates, {
      headers: { ...this.authHeaders(), 'Content-Type': 'application/json' }
    });

    logger.log('[LeadService] Updated lead:', id, updates);
    return response.data.lead;
  }

  /**
   * Suppress (or un-suppress) a lead from all future outreach campaigns.
   *
   * Stored separately from `status` so consent and pipeline state cannot
   * overwrite each other.
   *
   * @param {string} id
   * @param {boolean} unsubscribed
   * @returns {Promise<Object>} the updated lead
   */
  async setUnsubscribed(id, unsubscribed = true) {
    const response = await axios.post(
      `${API_BASE_URL}/api/notifications/leads/${id}/unsubscribe`,
      { unsubscribed },
      { headers: { ...this.authHeaders(), 'Content-Type': 'application/json' } }
    );

    logger.log('[LeadService] Set unsubscribed:', id, unsubscribed);
    return response.data.lead;
  }
}

export const leadService = new LeadService();
export default leadService;

export const LEAD_STATUSES = [
  'new',
  'contacted',
  'responded',
  'qualified',
  'converted',
  'rejected',
  'bounced'
];

/** Chip colour per status, so the table reads at a glance. */
export const LEAD_STATUS_COLORS = {
  new: 'default',
  contacted: 'info',
  responded: 'primary',
  qualified: 'success',
  converted: 'success',
  rejected: 'error',
  bounced: 'warning'
};
